import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CircleIcon from "@mui/icons-material/Circle";
import { useTranslation } from "react-i18next";
import { CodOrder, CodPaymentTransaction } from "../../../types/codSalesRep";
import {
  codInvoiceStatusKey,
  codInvoiceStatusTone,
  formatCodAmount,
  formatCodDate,
  getOrderOutstanding,
  getOrderPayableTotal,
} from "../../../utils/codUtils";
import {
  collectionRate,
  getOrderCollected,
  InvoiceIndex,
  orderBucket,
  orderDate,
  orderFinancials,
  ReportDateRange,
  ReportTotals,
} from "../../../utils/codReports";
import CodStatCard from "../../../components/ui/CodStatCard";
import StatusChip from "../../../components/ui/StatusChip";
import SearchField from "../../component/common/searchField";
import { NJIM, primaryBtnSx } from "./codStyles";

/* ── Toolbar ─────────────────────────────────────────────────────────────────── */

interface ReportToolbarProps {
  range: ReportDateRange;
  onRangeChange: (range: ReportDateRange) => void;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  onlyBalance: boolean;
  onOnlyBalanceChange: (value: boolean) => void;
  onExport: () => void;
  exporting: boolean;
  exportDisabled?: boolean;
  /** Label for the filter switch; defaults to "Only with balance". */
  toggleLabel?: string;
  /** Hide the date range (for data without dates). */
  hideDates?: boolean;
}

const dateFieldSx = {
  width: { xs: "100%", sm: 160 },
  "& .MuiOutlinedInput-root": { height: 44, borderRadius: "10px", background: NJIM.white },
} as const;

export const ReportToolbar: React.FC<ReportToolbarProps> = ({
  range,
  onRangeChange,
  search,
  onSearchChange,
  searchPlaceholder,
  onlyBalance,
  onOnlyBalanceChange,
  onExport,
  exporting,
  exportDisabled,
  toggleLabel,
  hideDates = false,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        mb: 2.5,
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${NJIM.border}`,
        background: NJIM.muted,
      }}
    >
      {!hideDates && (
      <>
      <TextField
        type="date"
        size="small"
        label={t("codReports.from")}
        value={range.from}
        onChange={(e) => onRangeChange({ ...range, from: e.target.value })}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: range.to || undefined }}
        sx={dateFieldSx}
      />
      <TextField
        type="date"
        size="small"
        label={t("codReports.to")}
        value={range.to}
        onChange={(e) => onRangeChange({ ...range, to: e.target.value })}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: range.from || undefined }}
        sx={dateFieldSx}
      />
      {(range.from || range.to) && (
        <Button
          size="small"
          startIcon={<EventBusyRoundedIcon />}
          onClick={() => onRangeChange({ from: "", to: "" })}
          sx={{ textTransform: "none", fontWeight: 600, color: NJIM.textSecondary }}
        >
          {t("codReports.clearDates")}
        </Button>
      )}
      </>
      )}
      <Box sx={{ flex: "1 1 220px", minWidth: 200 }}>
        <SearchField value={search} onChange={onSearchChange} placeholder={searchPlaceholder} />
      </Box>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={onlyBalance}
            onChange={(e) => onOnlyBalanceChange(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: NJIM.mint },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: NJIM.mint },
            }}
          />
        }
        label={
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: NJIM.ink }}>
            {toggleLabel ?? t("codReports.onlyWithBalance")}
          </Typography>
        }
        sx={{ m: 0, gap: 0.5 }}
      />
      <Button
        variant="contained"
        onClick={onExport}
        disabled={exporting || exportDisabled}
        startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadRoundedIcon />}
        sx={{ ...primaryBtnSx, marginInlineStart: { md: "auto" }, width: { xs: "100%", sm: "auto" } }}
      >
        {exporting ? t("codReports.exporting") : t("codReports.exportExcel")}
      </Button>
    </Box>
  );
};

/* ── KPI row ─────────────────────────────────────────────────────────────────── */

export const ReportKpis: React.FC<{
  totals: ReportTotals;
  countLabel: string;
  count: number;
  countIcon: React.ReactNode;
  loading?: boolean;
}> = ({ totals, countLabel, count, countIcon, loading }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ containerType: "inline-size" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 2,
          mb: 3,
          "@container (min-width: 620px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
          "@container (min-width: 980px)": { gridTemplateColumns: "repeat(5, minmax(0, 1fr))" },
        }}
      >
        <CodStatCard label={countLabel} value={count} tone="ink" icon={countIcon} loading={loading} />
        <CodStatCard
          label={`${t("codReports.invoiced")} · ${totals.invoiceCount} ${t("codReports.invoiceCount").toLowerCase()}`}
          value={formatCodAmount(totals.invoiced)}
          tone="ink"
          icon={<TaskAltRoundedIcon />}
          loading={loading}
          compactValue
        />
        <CodStatCard
          label={`${t("codReports.collected")} · ${(collectionRate(totals) * 100).toFixed(0)}%`}
          value={formatCodAmount(totals.collected)}
          tone="success"
          icon={<PaymentsRoundedIcon />}
          loading={loading}
          compactValue
        />
        <CodStatCard
          label={t("codReports.remaining")}
          value={formatCodAmount(totals.remaining)}
          tone="danger"
          icon={<AccountBalanceWalletRoundedIcon />}
          loading={loading}
          compactValue
        />
        <CodStatCard
          label={t("codReports.notInvoiced")}
          value={formatCodAmount(totals.notInvoiced)}
          tone="warning"
          icon={<ReceiptLongRoundedIcon />}
          loading={loading}
          compactValue
        />
      </Box>
    </Box>
  );
};

/* ── Small building blocks ───────────────────────────────────────────────────── */

export const CollectionBar: React.FC<{
  totals: Pick<ReportTotals, "approvedValue" | "collected"> & Partial<Pick<ReportTotals, "invoiced">>;
}> = ({ totals }) => {
  const rate = collectionRate(totals);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ height: 6, borderRadius: 999, bgcolor: NJIM.dangerWash, overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${Math.min(100, rate * 100)}%`, bgcolor: NJIM.success, borderRadius: 999 }} />
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 11, color: NJIM.textMuted, fontVariantNumeric: "tabular-nums" }}>
        {(rate * 100).toFixed(0)}%
      </Typography>
    </Box>
  );
};

export const MoneyStat: React.FC<{ label: string; value: number; color?: string; align?: "start" | "end" }> = ({
  label,
  value,
  color = NJIM.textPrimary,
  align = "end",
}) => (
  <Box sx={{ minWidth: 0, textAlign: align }}>
    <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 800, color, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {formatCodAmount(value)}
    </Typography>
  </Box>
);

/* ── Detail lists (orders + payments of one customer or rep) ─────────────────── */

const miniHeaderSx = {
  fontSize: 12,
  fontWeight: 700,
  color: NJIM.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  mb: 1,
} as const;

const miniRowSx = {
  display: "grid",
  alignItems: "center",
  gap: { xs: 1, sm: 2 },
  px: 1.5,
  py: 1,
  borderRadius: "10px",
  bgcolor: NJIM.muted,
  border: `1px solid ${NJIM.border}`,
} as const;

export const OrderStatusChip: React.FC<{ order: CodOrder }> = ({ order }) => {
  const { t } = useTranslation();
  const bucket = orderBucket(order);
  if (bucket === "pending") return <StatusChip status="pendingReview" label={t("codSalesRep.pendingReview")} />;
  if (bucket === "rejected") return <StatusChip status="rejected" label={t("codSalesRep.rejected")} />;
  if (getOrderOutstanding(order) <= 0.005 && getOrderPayableTotal(order) > 0)
    return <StatusChip status="paid" label={t("codSalesRep.paid")} />;
  if (getOrderCollected(order) > 0) return <StatusChip status="partlyPaid" label={t("codSalesRep.partlyPaid")} />;
  return <StatusChip status="draft" label={t("codSalesRep.awaitingPayment")} />;
};

export const ReportDetail: React.FC<{
  orders: CodOrder[];
  payments: CodPaymentTransaction[];
  allOrders: CodOrder[];
  /** Sage invoices per order — when present, each order lists its lines and invoices. */
  invoiceIndex?: InvoiceIndex;
}> = ({ orders, payments, allOrders, invoiceIndex }) => {
  const { t } = useTranslation();
  const orderById = new Map(allOrders.map((order) => [order.id, order]));
  const sortedOrders = [...orders].sort(
    (a, b) => new Date(orderDate(b) ?? 0).getTime() - new Date(orderDate(a) ?? 0).getTime()
  );
  const sortedPayments = [...payments].sort(
    (a, b) => new Date(b.paymentDate ?? 0).getTime() - new Date(a.paymentDate ?? 0).getTime()
  );

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1.4fr) minmax(0, 1fr)" }, gap: 2.5 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={miniHeaderSx}>
          {t("codReports.orders")} ({orders.length})
        </Typography>
        {sortedOrders.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: NJIM.textMuted }}>{t("codReports.noOrders")}</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 360, overflowY: "auto" }}>
            {sortedOrders.map((order) => {
              const approved = orderBucket(order) === "approved";
              const money = orderFinancials(order, invoiceIndex);
              const invoiceEntry = approved ? invoiceIndex?.get(order.id) : undefined;
              return (
                <Box key={order.id}>
                <Box
                  sx={{
                    ...miniRowSx,
                    gridTemplateColumns: { xs: "minmax(0, 1fr) auto", sm: "minmax(0, 1.3fr) auto auto auto" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {approved && (
                        <Tooltip title={order.isSynced ? t("codSalesRep.syncedToSage") : t("codSalesRep.notSyncedToSage")}>
                          <CircleIcon sx={{ fontSize: 10, color: order.isSynced ? NJIM.success : NJIM.warning }} />
                        </Tooltip>
                      )}
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary, whiteSpace: "nowrap" }}>
                        {order.number}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {formatCodDate(orderDate(order))}
                      {order.customerName ? ` · ${order.customerName}` : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ justifySelf: { xs: "end", sm: "start" } }}>
                    <OrderStatusChip order={order} />
                  </Box>
                  <MoneyStat label={t("codReports.total")} value={getOrderPayableTotal(order)} align="end" />
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    <MoneyStat
                      label={t("codReports.remaining")}
                      value={approved ? money.remaining : 0}
                      color={approved && money.remaining > 0.005 ? NJIM.danger : NJIM.textMuted}
                    />
                  </Box>
                </Box>
                {invoiceEntry && (
                  <Box sx={{ mt: 0.5, paddingInlineStart: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {invoiceEntry.lines.map((line) => (
                      <Box key={line.reference}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: NJIM.textSecondary }}>
                          {line.reference}
                          {line.category ? ` · ${line.category}` : ""}
                        </Typography>
                        {line.hasInvoice && line.invoices.length > 0 ? (
                          line.invoices.map((invoice) => (
                            <Box
                              key={invoice.invoiceId}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto auto",
                                alignItems: "center",
                                gap: 1,
                                px: 1,
                                py: 0.5,
                                borderRadius: "8px",
                                bgcolor: NJIM.white,
                                border: `1px solid ${NJIM.border}`,
                                mt: 0.25,
                              }}
                            >
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: NJIM.textPrimary }}>
                                {t("codSalesRep.invoiceNo", { number: invoice.invoiceNumber })}
                              </Typography>
                              <StatusChip
                                status={codInvoiceStatusTone(invoice.paymentStatus)}
                                label={t(codInvoiceStatusKey(invoice.paymentStatus))}
                              />
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: invoice.remainingAmount > 0.005 ? NJIM.danger : NJIM.success, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                {formatCodAmount(invoice.remainingAmount, invoiceEntry.currency)} / {formatCodAmount(invoice.amount, invoiceEntry.currency)}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography sx={{ fontSize: 11, color: NJIM.warning, fontWeight: 600, mt: 0.25 }}>
                            {t("codSalesRep.awaitingSageInvoice")}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={miniHeaderSx}>
          {t("codReports.payments")} ({payments.length})
        </Typography>
        {sortedPayments.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: NJIM.textMuted }}>{t("codReports.noPayments")}</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 360, overflowY: "auto" }}>
            {sortedPayments.map((payment) => (
              <Box key={payment.id || payment.transactionId} sx={{ ...miniRowSx, gridTemplateColumns: "minmax(0, 1fr) auto" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {payment.paymentReference
                      ? `${t("codReports.paymentReference")}: ${payment.paymentReference}`
                      : `${t("codReports.receiptNo")} ${payment.documentReceiptNumber || "—"}`}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {formatCodDate(payment.paymentDate)} · {orderById.get(payment.orderId)?.number ?? "—"}
                    {payment.paymentReference && payment.documentReceiptNumber
                      ? ` · ${t("codReports.receiptNo")} ${payment.documentReceiptNumber}`
                      : ""}
                    {payment.isPartialPayment ? ` · ${t("codReports.partial")}` : ""}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: NJIM.success, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {formatCodAmount(payment.amount, payment.currency)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ── Expandable row shell ────────────────────────────────────────────────────── */

export const reportRowSx = (open: boolean) =>
  ({
    borderRadius: "14px",
    border: `1px solid ${open ? NJIM.mint : NJIM.border}`,
    background: NJIM.white,
    boxShadow: open ? NJIM.elevationHover : "none",
    overflow: "hidden",
    transition: `border-color 150ms ${NJIM.easeOut}, box-shadow 150ms ${NJIM.easeOut}`,
  }) as const;

export const reportRowHeaderSx = {
  display: "grid",
  alignItems: "center",
  gap: { xs: 1.5, md: 2.5 },
  p: { xs: 1.5, md: 2 },
  cursor: "pointer",
  "&:hover": { background: NJIM.muted },
  "&:focus-visible": { outline: `2px solid ${NJIM.mint}`, outlineOffset: -2 },
} as const;

export const onActivate = (fn: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fn();
  }
};
